// ABOUTME: The 1A "RESET FOR" options: a labelled group of three breathing lengths over the dark mask, fading in and out over 0.7 s.
// ABOUTME: Presentation only; it reports the chosen length in seconds and the first option takes focus when it opens.

import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { keyframes } from '@emotion/react';
import {
  CROSSING_PILL_BG, CROSSING_PILL_BORDER, CROSSING_PILL_INK, CROSSING_STEERSMAN_GROUND, CROSSING_GROUND,
  CROSSING_RESET_MASK_TEXT_SHADOW,
} from '../../../../theme/crossingTokens';

const pvOptionsIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;
const pvOptionsOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const DURATIONS: { secs: number; label: string }[] = [
  { secs: 60, label: '1 min' },
  { secs: 180, label: '3 min' },
  { secs: 300, label: '5 min' },
];

interface DurationPickerProps {
  onPick: (secs: number) => void;
  leaving?: boolean; // a length was picked: the options fade out and take no input
  reducedMotion?: boolean;
}

export default function DurationPicker({ onPick, leaving = false, reducedMotion = false }: DurationPickerProps) {
  return (
    <Box
      data-testid="duration-picker-layer"
      inert={leaving}
      aria-hidden={leaving || undefined}
      sx={{
        position: 'absolute', inset: 0, zIndex: 3, display: 'flex', alignItems: 'center', justifyContent: 'center',
        // The keyframes object is interpolated, not passed, so emotion injects its CSS when the class is built.
        animationName: reducedMotion ? 'none' : `${leaving ? pvOptionsOut : pvOptionsIn}`,
        animationDuration: '0.7s', animationTimingFunction: 'ease', animationFillMode: 'forwards',
        pointerEvents: leaving ? 'none' : 'auto',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <Box sx={{ fontSize: '11px', fontWeight: 800, letterSpacing: '2.4px', color: CROSSING_GROUND, textShadow: `0 1px 6px ${CROSSING_RESET_MASK_TEXT_SHADOW}` }}>RESET FOR</Box>
        <Box data-testid="duration-picker" role="group" aria-label="Choose a breathing duration" sx={{ display: 'flex', gap: '12px' }}>
          {DURATIONS.map(({ secs, label }, index) => (
            <Box
              key={secs}
              component="button"
              type="button"
              autoFocus={index === 0}
              onClick={() => onPick(secs)}
              sx={{
                background: CROSSING_PILL_BG,
                border: `1.5px solid ${CROSSING_PILL_BORDER}`,
                color: CROSSING_PILL_INK,
                fontSize: '14px',
                fontWeight: 800,
                borderRadius: '999px',
                padding: '11px 24px',
                cursor: 'pointer',
                boxShadow: `0 2px 8px ${alpha(CROSSING_PILL_INK, 0.12)}`,
                '&:hover': { background: CROSSING_STEERSMAN_GROUND },
              }}
            >
              {label}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
